import { useEffect, useMemo, useState } from "react";
import { Clock, Download, GripVertical, LayoutGrid, List } from "lucide-react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Select } from "antd";
import { useQuery } from "@tanstack/react-query";
import { PtsProjectService, DropdownService } from "@/services/APIs";
import { downloadCSV } from "@/lib/downloadCSV";
import { useNavigate } from "react-router-dom";
import PTSNavigationRoutes from "../../utils/navigationRoutes";
import { toast } from "../../components/ui/sonner";

const STATUS_SCHEMA_NAME = "PTS Status";
const WORK_TYPE_SCHEMA_NAME = "PTS Work Type";

const STATUS_COLOR_PALETTE = [
  "border-slate-400 bg-slate-50",
  "border-blue-400 bg-blue-50",
  "border-violet-400 bg-violet-50",
  "border-indigo-400 bg-indigo-50",
  "border-cyan-400 bg-cyan-50",
  "border-amber-400 bg-amber-50",
  "border-orange-400 bg-orange-50",
  "border-pink-400 bg-pink-50",
  "border-emerald-400 bg-emerald-50",
  "border-green-400 bg-green-50",
  "border-teal-400 bg-teal-50",
  "border-sky-400 bg-sky-50",
  "border-lime-400 bg-lime-50",
  "border-gray-400 bg-gray-100",
];

interface StatusColumn {
  id: string;
  label: string;
}

type ViewMode = "kanban" | "list";

interface KanbanTask {
  task_id: string;
  task_name: string;
  task_status: string;
  task_estimation_hours: number | null;
  work_type?:
    | string
    | {
        value?: string;
        label?: string;
        id?: string;
        name?: string;
      }
    | null;
  project_id?: string;
  project_name?: string;
}

interface KanbanStatusData {
  count?: number;
  task_listing?: KanbanTask[];
}

type ProjectStatusCountValue = number | KanbanStatusData;

interface ProjectSummaryData {
  project_id: string;
  project_name: string;
  status_counts: Record<string, ProjectStatusCountValue>;
}

interface KanbanColumn {
  count: number;
  tasks: KanbanTask[];
}

const getStatusValueCount = (value?: ProjectStatusCountValue): number => {
  if (typeof value === "number") {
    return value;
  }
  if (value && typeof value === "object") {
    return value.count ?? value.task_listing?.length ?? 0;
  }
  return 0;
};

const matchStatusColumn = (
  statusColumns: StatusColumn[],
  statusKey: string
): StatusColumn | undefined =>
  statusColumns.find(
    column =>
      column.id === statusKey ||
      column.label === statusKey ||
      column.label.toLowerCase() === statusKey.toLowerCase()
  );

const createEmptyKanbanColumns = (
  statusColumns: StatusColumn[]
): Record<string, KanbanColumn> =>
  statusColumns.reduce(
    (acc, column) => {
      acc[column.id] = { count: 0, tasks: [] };
      return acc;
    },
    {} as Record<string, KanbanColumn>
  );

const normalizeKanbanColumns = (
  statusColumns: StatusColumn[],
  projects?: ProjectSummaryData[]
): Record<string, KanbanColumn> => {
  const columns = createEmptyKanbanColumns(statusColumns);

  if (!projects) {
    return columns;
  }

  projects.forEach(project => {
    Object.entries(project.status_counts ?? {}).forEach(
      ([statusName, statusValue]) => {
        const matchedColumn = matchStatusColumn(statusColumns, statusName);
        if (!matchedColumn) {
          return;
        }

        const tasks =
          typeof statusValue === "object" &&
          Array.isArray(statusValue.task_listing)
            ? statusValue.task_listing.map(task => ({
                ...task,
                project_id: project.project_id,
                project_name: project.project_name,
              }))
            : [];

        columns[matchedColumn.id] = {
          count:
            columns[matchedColumn.id].count + getStatusValueCount(statusValue),
          tasks: [...columns[matchedColumn.id].tasks, ...tasks],
        };
      }
    );
  });

  return columns;
};

const moveKanbanTask = (
  columns: Record<string, KanbanColumn>,
  sourceStatus: string,
  destinationStatus: string,
  sourceIndex: number,
  destinationIndex: number
): { nextColumns: Record<string, KanbanColumn>; movedTask?: KanbanTask } => {
  const sourceColumn = columns[sourceStatus];
  const destinationColumn = columns[destinationStatus];

  if (!sourceColumn || !destinationColumn) {
    return { nextColumns: columns };
  }

  const sourceTasks = [...sourceColumn.tasks];
  const [movedTask] = sourceTasks.splice(sourceIndex, 1);

  if (!movedTask) {
    return { nextColumns: columns };
  }

  const updatedTask = {
    ...movedTask,
    task_status: destinationStatus,
  };

  if (sourceStatus === destinationStatus) {
    sourceTasks.splice(destinationIndex, 0, updatedTask);

    return {
      nextColumns: {
        ...columns,
        [sourceStatus]: {
          ...sourceColumn,
          count: sourceTasks.length,
          tasks: sourceTasks,
        },
      },
      movedTask: updatedTask,
    };
  }

  const destinationTasks = [...destinationColumn.tasks];
  destinationTasks.splice(destinationIndex, 0, updatedTask);

  return {
    nextColumns: {
      ...columns,
      [sourceStatus]: {
        ...sourceColumn,
        count: sourceTasks.length,
        tasks: sourceTasks,
      },
      [destinationStatus]: {
        ...destinationColumn,
        count: destinationTasks.length,
        tasks: destinationTasks,
      },
    },
    movedTask: updatedTask,
  };
};

const ProjectSummaryPage = () => {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<string>("all");
  const [activeView, setActiveView] = useState<ViewMode>("kanban");
  const [kanbanColumns, setKanbanColumns] = useState<
    Record<string, KanbanColumn>
  >({});

  // Fetch project dropdown options
  const { data: projectOptions } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  // Fetch status options for dynamic columns
  const { data: statusOptions, isLoading: isStatusLoading } = useQuery({
    queryKey: ["status-dropdown", STATUS_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(STATUS_SCHEMA_NAME),
    enabled: !!STATUS_SCHEMA_NAME,
  });

  const statusColumns = useMemo<StatusColumn[]>(() => {
    if (!statusOptions?.length) {
      return [];
    }

    return statusOptions.map((option: { value: string; label: string }) => ({
      id: String(option.value),
      label: option.label,
    }));
  }, [statusOptions]);

  const { data: workTypeOptions } = useQuery({
    queryKey: ["work-type-dropdown", WORK_TYPE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(WORK_TYPE_SCHEMA_NAME),
    enabled: activeView === "kanban",
  });

  // Fetch project summary data
  const { data: summaryData, isLoading } = useQuery({
    queryKey: ["project-summary", selectedProject],
    queryFn: () => {
      const projectId = selectedProject === "all" ? undefined : selectedProject;
      return PtsProjectService.getProjectSummary(projectId);
    },
    enabled: activeView === "list",
  });

  // Fetch project summary kanban data only when kanban tab is active
  const { data: kanbanSummaryData, isLoading: isKanbanLoading } = useQuery({
    queryKey: ["project-summary-kanban", selectedProject],
    queryFn: () => {
      const projectId = selectedProject === "all" ? undefined : selectedProject;
      return PtsProjectService.getProjectSummaryKanban(projectId);
    },
    enabled: activeView === "kanban",
  });

  // Extract projects from response
  const projects: ProjectSummaryData[] = useMemo(() => {
    if (!summaryData?.projects) {
      return [];
    }
    return summaryData.projects;
  }, [summaryData]);

  const normalizedWorkTypeOptions = useMemo(
    () =>
      (workTypeOptions ?? []) as Array<{
        value?: string;
        label?: string;
        id?: string;
        name?: string;
      }>,
    [workTypeOptions]
  );

  const normalizedKanbanColumns = useMemo(
    () => normalizeKanbanColumns(statusColumns, kanbanSummaryData?.projects),
    [statusColumns, kanbanSummaryData]
  );

  useEffect(() => {
    setKanbanColumns(normalizedKanbanColumns);
  }, [normalizedKanbanColumns]);

  // Handle export to CSV
  const handleExport = () => {
    if (!projects || projects.length === 0 || statusColumns.length === 0) {
      return;
    }

    const headers = [
      "Project/Status",
      ...statusColumns.map(column => column.label),
    ];

    const rows = projects.map(project => {
      const row: (string | number)[] = [project.project_name];
      statusColumns.forEach(column => {
        const statusKey = Object.keys(project.status_counts).find(key =>
          matchStatusColumn([column], key)
        );
        const count = statusKey
          ? getStatusValueCount(project.status_counts[statusKey])
          : 0;
        row.push(count);
      });
      return row;
    });

    // Convert to CSV format
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(",")),
    ].join("\n");

    const filename = `project-summary-${new Date().toISOString().split("T")[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  // Get status count for a project and status
  const getStatusCount = (
    project: ProjectSummaryData,
    column: StatusColumn
  ): number => {
    const statusKey = Object.keys(project.status_counts).find(key =>
      matchStatusColumn([column], key)
    );
    return statusKey
      ? getStatusValueCount(project.status_counts[statusKey])
      : 0;
  };

  const getStatusId = (statusColumnId: string): string | null => {
    const column = statusColumns.find(item => item.id === statusColumnId);
    return column?.id ?? null;
  };

  const handleStatusClick = (projectId: string, statusColumnId: string) => {
    const statusId = getStatusId(statusColumnId);
    // Navigate to Task page with project and status filters using the specific route
    const taskRoute = PTSNavigationRoutes.TASKS_WITH_FILTERS(
      projectId,
      statusId ?? undefined
    );
    navigate(taskRoute);
  };

  const getWorkTypeLabel = (workType?: KanbanTask["work_type"]): string => {
    if (!workType) {
      return "N/A";
    }

    if (typeof workType === "object") {
      return (
        workType.label ??
        workType.name ??
        workType.value ??
        workType.id ??
        "N/A"
      );
    }

    const option = normalizedWorkTypeOptions.find(
      item => String(item?.value ?? item?.id ?? "") === String(workType)
    );

    return (option?.label ?? option?.name ?? workType) as string;
  };

  const handleKanbanDragEnd = async (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    const sourceStatus = source.droppableId;
    const destinationStatus = destination.droppableId;

    if (
      !sourceStatus ||
      !destinationStatus ||
      (sourceStatus === destinationStatus && source.index === destination.index)
    ) {
      return;
    }

    const previousColumns = kanbanColumns;
    const { nextColumns, movedTask } = moveKanbanTask(
      previousColumns,
      sourceStatus,
      destinationStatus,
      source.index,
      destination.index
    );

    if (!movedTask) {
      return;
    }

    setKanbanColumns(nextColumns);

    if (sourceStatus === destinationStatus) {
      return;
    }

    const destinationStatusId = getStatusId(destinationStatus);

    if (!destinationStatusId) {
      setKanbanColumns(previousColumns);
      toast.error("Status update failed", {
        description: "Unable to resolve the destination status.",
      });
      return;
    }

    const response = await PtsProjectService.updateTaskStatus(
      movedTask.task_id,
      destinationStatusId
    );

    if (!response) {
      setKanbanColumns(previousColumns);
      toast.error("Status update failed", {
        description: "Task status could not be updated. Please try again.",
      });
    }
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Project Summary
          </h1>
        </div>
        <Button
          onClick={handleExport}
          className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
        >
          <Download className="h-4 w-4" />
          Export
        </Button>
      </div>

      {/* Project Filter */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-[#111827]">
              Select Project:
            </label>
            <Select
              value={selectedProject}
              onChange={setSelectedProject}
              className="w-48"
              placeholder="All Projects"
              showSearch
              allowClear
              filterOption={(input, option) => {
                const label = String(option?.label ?? "");
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              options={[
                { value: "all", label: "All Projects" },
                ...(projectOptions?.map(
                  (project: { value: string; label: string }) => ({
                    value: project.value,
                    label: project.label,
                  })
                ) ?? []),
              ]}
            >
              <Select.Option value="all">All Projects</Select.Option>
              {projectOptions?.map(
                (project: { value: string; label: string }) => (
                  <Select.Option key={project.value} value={project.value}>
                    {project.label}
                  </Select.Option>
                )
              )}
            </Select>
          </div>
        </CardContent>
      </Card>

      <Tabs
        value={activeView}
        onValueChange={value => setActiveView(value as ViewMode)}
        className="space-y-4"
      >
        <TabsList className="h-auto w-fit bg-gray-100 p-2">
          <TabsTrigger
            value="kanban"
            className="flex items-center gap-2 data-[state=active]:border-b-0 data-[state=active]:bg-white data-[state=active]:shadow-md"
          >
            <LayoutGrid className="h-4 w-4" />
            Kanban View
          </TabsTrigger>
          <TabsTrigger
            value="list"
            className="flex items-center gap-2 data-[state=active]:border-b-0 data-[state=active]:bg-white data-[state=active]:shadow-md"
          >
            <List className="h-4 w-4" />
            List View
          </TabsTrigger>
        </TabsList>

        <TabsContent value="kanban" className="mt-0">
          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-0">
              <div className="border-b border-gray-200 p-4">
                <h2 className="text-base font-semibold text-[#111827]">
                  Project Summary Kanban
                </h2>
              </div>

              {isStatusLoading || isKanbanLoading ? (
                <div className="p-8 text-center text-xs text-gray-500">
                  Loading...
                </div>
              ) : statusColumns.length === 0 ? (
                <div className="p-8 text-center text-xs text-gray-500">
                  No status data available
                </div>
              ) : (
                <div className="p-4">
                  <DragDropContext onDragEnd={handleKanbanDragEnd}>
                    <div className="scrollbar-thin-grey w-full overflow-x-auto">
                      <div className="flex min-w-max gap-3 pb-4">
                        {statusColumns.map((column, columnIndex) => {
                          const kanbanColumn = kanbanColumns[column.id] ?? {
                            count: 0,
                            tasks: [],
                          };

                          return (
                            <Droppable key={column.id} droppableId={column.id}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  className={`bg-muted/30 min-h-[70vh] w-[260px] rounded-lg border ${
                                    snapshot.isDraggingOver
                                      ? "ring-primary/40 bg-primary/5 ring-2"
                                      : ""
                                  }`}
                                >
                                  <div
                                    className={`rounded-t-lg border-t-4 px-3 py-2 ${STATUS_COLOR_PALETTE[columnIndex % STATUS_COLOR_PALETTE.length]}`}
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="text-foreground text-xs font-semibold">
                                        {column.label}
                                      </span>
                                      <Badge
                                        variant="outline"
                                        className="h-5 min-w-[20px] justify-center text-[10px]"
                                      >
                                        {kanbanColumn.count}
                                      </Badge>
                                    </div>
                                  </div>

                                  <div className="scrollbar-thin-grey max-h-[65vh] space-y-2 overflow-y-auto p-2">
                                    {kanbanColumn.tasks.map((task, index) => (
                                      <Draggable
                                        key={task.task_id}
                                        draggableId={task.task_id}
                                        index={index}
                                      >
                                        {(dragProvided, snapshot) => (
                                          <div
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            className={`rounded-xl border bg-white p-3 shadow-sm transition-shadow ${
                                              snapshot.isDragging
                                                ? "ring-primary/30 shadow-lg ring-2"
                                                : "hover:shadow-md"
                                            }`}
                                          >
                                            <div className="flex items-start gap-2">
                                              <div
                                                {...dragProvided.dragHandleProps}
                                                className="text-muted-foreground/50 mt-1"
                                              >
                                                <GripVertical className="h-3.5 w-3.5" />
                                              </div>

                                              <div className="min-w-0 flex-1 space-y-2">
                                                <div>
                                                  <p className="truncate text-sm font-semibold text-[#1f3b67]">
                                                    {task.task_name ?? "-"}
                                                  </p>
                                                  <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">
                                                    {task.project_name ?? "-"}
                                                  </p>
                                                </div>

                                                <div className="flex items-center justify-between gap-2">
                                                  <Badge
                                                    variant="outline"
                                                    className="rounded-full border-orange-200 bg-orange-50 px-2 py-0 text-[10px] font-medium text-orange-700"
                                                  >
                                                    {getWorkTypeLabel(
                                                      task.work_type
                                                    )}
                                                  </Badge>
                                                  <div className="text-muted-foreground flex items-center gap-1 text-[10px]">
                                                    <Clock className="h-3 w-3" />
                                                    {task.task_estimation_hours ??
                                                      0}
                                                    h
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </Draggable>
                                    ))}
                                    {provided.placeholder}
                                  </div>
                                </div>
                              )}
                            </Droppable>
                          );
                        })}
                      </div>
                    </div>
                  </DragDropContext>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="list" className="mt-0">
          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-0">
              <div className="border-b border-gray-200 p-4">
                <h2 className="text-base font-semibold text-[#111827]">
                  Project Summary (Universal)
                </h2>
              </div>
              <div className="scrollbar-thin-grey max-h-[60vh] overflow-auto">
                <table className="w-full caption-bottom text-sm">
                  <thead className="[&_tr]:border-b">
                    <tr className="sticky top-0 z-20 border-b bg-gray-100 transition-colors">
                      <th className="bg-primary text-primary-foreground sticky left-0 top-0 z-30 h-12 min-w-[140px] px-4 py-2 text-left align-middle text-xs font-semibold shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] [&:has([role=checkbox])]:pr-0">
                        Project/Status
                      </th>
                      {statusColumns.map(column => {
                        const getMinWidth = (currentStatus: string) => {
                          if (currentStatus.length <= 4) {
                            return "60px";
                          }
                          if (currentStatus.length <= 8) {
                            return "70px";
                          }
                          if (currentStatus.length <= 10) {
                            return "80px";
                          }
                          return "90px";
                        };

                        return (
                          <th
                            key={column.id}
                            className="text-muted-foreground sticky top-0 z-20 h-12 min-w-[60px] bg-gray-100 px-4 py-2 text-center align-middle text-xs font-medium [&:has([role=checkbox])]:pr-0"
                            style={{ minWidth: getMinWidth(column.label) }}
                          >
                            {column.label}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="[&amp;_tr:last-child]:border-0">
                    {isStatusLoading || isLoading ? (
                      <tr className="data-[state=selected]:bg-muted hover:bg-muted/50 border-b transition-colors">
                        <td
                          colSpan={statusColumns.length + 1}
                          className="[&amp;:has([role=checkbox])]:pr-0 p-8 text-center align-middle text-xs text-gray-500"
                        >
                          Loading...
                        </td>
                      </tr>
                    ) : statusColumns.length === 0 ? (
                      <tr className="data-[state=selected]:bg-muted hover:bg-muted/50 border-b transition-colors">
                        <td
                          colSpan={2}
                          className="[&amp;:has([role=checkbox])]:pr-0 p-8 text-center align-middle text-xs text-gray-500"
                        >
                          No status data available
                        </td>
                      </tr>
                    ) : projects.length === 0 ? (
                      <tr className="data-[state=selected]:bg-muted hover:bg-muted/50 border-b transition-colors">
                        <td
                          colSpan={statusColumns.length + 1}
                          className="[&amp;:has([role=checkbox])]:pr-0 p-8 text-center align-middle text-xs text-gray-500"
                        >
                          No data available
                        </td>
                      </tr>
                    ) : (
                      projects.map(project => (
                        <tr
                          key={project.project_id}
                          className="data-[state=selected]:bg-muted hover:bg-muted/50 border-b transition-colors"
                        >
                          <td className="[&amp;:has([role=checkbox])]:pr-0 sticky left-0 z-10 bg-[#eef6fe] p-4 py-2 align-middle text-xs font-semibold">
                            {project.project_name}
                          </td>
                          {statusColumns.map(column => {
                            const count = getStatusCount(project, column);
                            const hasCount = count > 0;

                            return (
                              <td
                                key={column.id}
                                className={`[&amp;:has([role=checkbox])]:pr-0 p-4 py-2 text-center align-middle text-xs ${
                                  hasCount
                                    ? "hover:bg-muted/50 cursor-pointer"
                                    : ""
                                }`}
                                onClick={() => {
                                  if (hasCount) {
                                    handleStatusClick(
                                      project.project_id,
                                      column.id
                                    );
                                  }
                                }}
                              >
                                {hasCount ? (
                                  <span className="text-primary font-medium hover:underline">
                                    {count}
                                  </span>
                                ) : (
                                  ""
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProjectSummaryPage;
